import React from "react";
import { useDarkMode } from "../../contexts/DarkModeContext";

// Inlined styles to replace missing hrmStyles.ts
const hrmGridStyles = {
    stats: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
        gap: "24px",
    } as React.CSSProperties,
};

const getStatCardStyles = (isDarkMode: boolean): React.CSSProperties => ({
    backgroundColor: isDarkMode ? "#1e293b" : "#ffffff",
    padding: "24px",
    borderRadius: "12px",
    border: isDarkMode ? "1px solid #334155" : "1px solid #E5E7EB",
    boxShadow: isDarkMode ? "0 1px 2px 0 rgba(0, 0, 0, 0.2)" : "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
});

const createIconBadgeStyles = (color: string, isDarkMode: boolean): React.CSSProperties => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "48px",
    height: "48px",
    borderRadius: "50%",
    backgroundColor: isDarkMode ? `${color}30` : `${color}15`,
    color: color,
    fontSize: "24px",
});

export type HrmStatCard = {
    id: string;
    label: string;
    value: string | number;
    description?: string;
    accentColor: string;
    icon: React.ReactNode;
};

export type HrmStatCardsProps = {
    stats?: HrmStatCard[];
    variant?: "default" | "compact";
};

const HrmStatCards: React.FC<HrmStatCardsProps> = ({ stats = [], variant = "default" }) => {
    const { isDarkMode } = useDarkMode();
    const gridStyles: React.CSSProperties = variant === "compact"
        ? { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "18px" }
        : hrmGridStyles.stats;
    const cardStyle = getStatCardStyles(isDarkMode);
    const labelColor = isDarkMode ? "#94a3b8" : "#6B7280";
    const valueColor = isDarkMode ? "#f1f5f9" : "#111827";
    const descColor = isDarkMode ? "#94a3b8" : "#6B7280";

    return (
        <div style={gridStyles}>
            {stats.map((stat) => (
                <div key={stat.id} style={cardStyle}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                            <p style={{ fontSize: "14px", fontWeight: 600, color: labelColor, margin: 0, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                {stat.label}
                            </p>
                            <p style={{ fontSize: "36px", fontWeight: 700, color: valueColor, margin: 0, lineHeight: 1.1 }}>
                                {stat.value}
                            </p>
                        </div>
                        <span style={createIconBadgeStyles(stat.accentColor, isDarkMode)}>{stat.icon}</span>
                    </div>
                    {stat.description && (
                        <p style={{ fontSize: "14px", color: descColor, margin: 0 }}>
                            {stat.description}
                        </p>
                    )}
                </div>
            ))}
        </div>
    );
};

export default HrmStatCards;
